import { css } from '@emotion/css';
import { AreaOfInterest, Image, TextChunkBlock } from 'api/graphql/generated';
import { useRouter } from 'next/router';

import { Color } from '../../../css/theme';
import * as SegmentRe from 'bindings/Segment.bs';
import { SegmentUtils } from 'utils/SegmentUtils.bs';
import CmsImage from 'components/CmsImage';
import useSmartLink from 'hooks/useSmartLink';

type CCEnquiresProps = {
  block: AreaOfInterest;
};

type EnquiryBoxProps = {
  card: TextChunkBlock;
};

const Styles = {
  container: css`
    display: flex;
    flex-direction: column;
    padding-bottom: 1.5rem;
  `,
  title: css`
    font-size: 1.5rem;
    line-height: 2rem;
    color: ${Color.Gray800};
    font-weight: bold;
    margin-bottom: 1rem;
  `,
};

const BoxStyles = {
  boxContainer: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    @media (max-width: 1024px) {
      flex-direction: column;
    }
    flex-wrap: wrap;
  `,
  box: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 3rem 1.25rem;
    border: 1px solid ${Color.Gray200};
    border-radius: 4px;
    width: 32%;
    margin-bottom: 1rem;
    @media (max-width: 600px) {
      padding: 2rem 1.25rem;
    }
    @media (max-width: 1024px) {
      width: 100%;
      padding: 2.5rem 1.25rem;
      margin-right: 0;
    } ;
  `,
  title: css`
    font-weight: bold;
    color: ${Color.Purple400};
    font-size: 1rem;
    line-height: 1.5rem;
    width: 100%;
  `,
  arrowIcon: css`
    margin-left: 1rem;
    width: 1.5rem;
  `,
};

const EnquiryBox = ({ card }: EnquiryBoxProps) => {
  const { title, link, supportingAsset, _system_ } = card;
  const spotlightProps = useSmartLink(_system_);
  const { asPath } = useRouter();

  const imageUrl = supportingAsset.__typename === 'Image' ? supportingAsset.file.url : '/images/about/arrow-right.svg';
  const imageWidth = supportingAsset.__typename === 'Image' ? supportingAsset.file.width : supportingAsset.svg.width;
  const imageHeight = supportingAsset.__typename === 'Image' ? supportingAsset.file.height : supportingAsset.svg.height;

  return (
    <a
      onClick={async () => {
        const props = {
          element: SegmentUtils.mapElement(title),
          path: asPath,
        };
        await SegmentRe.customElementInteractionAsync({ ...props });
        window.open(link, '_self');
      }}
      className={BoxStyles.box}
      {...spotlightProps}
    >
      <h3 className={BoxStyles.title}>{title}</h3>
      <CmsImage
        className={BoxStyles.arrowIcon}
        src={imageUrl}
        alt="icon"
        width={imageWidth || 0}
        height={imageHeight || 0}
        sizes={'22px'}
      />
    </a>
  );
};

const CustomerCareEnquiries = ({ block }: CCEnquiresProps) => {
  const spotlightProps = useSmartLink(block._system_);

  return (
    <div className={Styles.container} {...spotlightProps}>
      <h2 className={Styles.title}>{block.areaHeader?.title || 'Top Enquiries'}</h2>
      <div className={BoxStyles.boxContainer}>
        {block.addressedNeeds.items.map((card, index) => (
          <EnquiryBox card={card} key={index} />
        ))}
      </div>
    </div>
  );
};

export default CustomerCareEnquiries;
